import React from 'react';
import { CheckCircle, Copy } from 'lucide-react';

const DonationSuccessView = ({ paymentData, primaryColor, businessId, business, onBack }) => {
  const totalImpact = (paymentData.amount || 0) + (paymentData.employer_match || 0);
  const charityName = paymentData.charity_name || 'charity';
  const companyName = business.name || 'this company';
  const shareText = `I just donated $${paymentData.amount} to ${charityName} through ${companyName}'s giving program on Altrunic! ${paymentData.employer_match > 0 ? `My employer matched it — $${totalImpact} total impact!` : ''} Join the movement: altrunic.com`;
  const shareUrl = `https://altrunic.com/impact/${businessId}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${primaryColor}08 0%, #f9fafb 50%, ${primaryColor}05 100%)`, fontFamily: "'DM Sans', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full overflow-hidden">
        {/* Success header */}
        <div className="text-center px-8 pt-10 pb-6">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center bg-emerald-50">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Thank you{paymentData.donor_name ? `, ${paymentData.donor_name.split(' ')[0]}` : ''}!</h1>
          <p className="text-sm text-gray-500">Your donation is making a real difference.</p>
        </div>

        {/* Amount breakdown */}
        <div className="mx-8 mb-6 rounded-xl bg-gray-50 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Your donation</span>
            <span className="text-xl font-bold" style={{ color: primaryColor }}>${paymentData.amount?.toLocaleString()}</span>
          </div>
          {paymentData.employer_match > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Employer match</span>
                <span className="text-xl font-bold text-emerald-600">+${paymentData.employer_match?.toLocaleString()}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total impact</span>
                <span className="text-2xl font-extrabold text-gray-900">${totalImpact.toLocaleString()}</span>
              </div>
            </>
          )}
          {charityName && charityName !== 'charity' && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500">Donated to <span className="font-semibold text-gray-700">{charityName}</span></p>
              <p className="text-[10px] text-gray-400 mt-0.5">Tax receipt sent to your email by Every.org</p>
            </div>
          )}
        </div>

        {/* Social sharing */}
        <div className="mx-8 mb-6">
          <p className="text-xs font-semibold text-gray-700 mb-3 text-center">Share your impact</p>
          <div className="flex gap-2">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-twitter"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-gray-800 transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Post
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-linkedin"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#004182] transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              Share
            </a>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(shareText);
                alert('Copied to clipboard!');
              }}
              data-testid="share-copy"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy
            </button>
          </div>
        </div>

        {/* Back button */}
        <div className="px-8 pb-8">
          <button
            onClick={onBack}
            data-testid="back-to-portal"
            className="w-full py-3 text-white rounded-xl font-semibold text-sm transition-all hover:shadow-lg"
            style={{ background: primaryColor }}
          >
            Back to Impact Portal
          </button>
        </div>

        {/* Powered by */}
        <div className="bg-gray-50 border-t border-gray-100 py-3 text-center">
          <p className="text-[10px] text-gray-400">
            Powered by <a href="https://altrunic.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-500 hover:text-gray-700">Altrunic</a> — corporate giving, finally simple.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationSuccessView;
